import { useState, useRef, useEffect, memo, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { EDSTextField } from '@gehc-eds-react/eds-text-field';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { EDSIcon } from '@gehc-eds-react/eds-icon';
import { EDSModal } from '@gehc-eds-react/eds-modal';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { createDataset, clearCreatedDataset } from '../../redux/slices/datasetSlice';
import { formatFileSize } from '../../utils/formatUtils';
import { startUploadSession } from '../../redux/slices/uploadProgressSlice';
import { startBackgroundUpload } from '../../services/uploadOrchestrator';
import { addErrorNotification, addSuccessNotification, addInfoNotification, addWarningNotification } from '../../redux/slices/notificationSlice';
import { ERROR_CODES, getErrorMessage } from '../../constants/errorCodes';
import { isExtensionValueAllowed, getFileExtension, isDangerousExtension } from '../../constants/fileValidation';
import { PASSWORD_PROTECTED_FILE_WARNING, LOCKED_FILE_WARNING, FILE_UPLOAD_INSTRUCTION, FILE_PROCESSING_WARNING } from '../../constants/uploadConfig';
import { validateFiles, buildSkipMessage, validateUploadLimits, getFilesWithUnsupportedNames } from '../../utils/fileValidationUtils';
import { useSelector } from 'react-redux';
import { selectDisallowedFilenameChars } from '../../redux/slices/fileExtensionsSlice';
import logger from '../../utils/logger';
import SpinnerComponent from '../../components/SpinnerComponent/SpinnerComponent';
import FilenameErrorModal from '../../components/FilenameErrorModal/FilenameErrorModal';
import { TAG_CONFIG } from '../../constants/createUpload';
// Helper function to traverse directory entries from drag and drop
const getAllFileEntries = async (dataTransferItemList) => {
    const fileEntries = [];
    const queue = [];

    // Add all items to queue
    for (let i = 0; i < dataTransferItemList.length; i++) {
        const item = dataTransferItemList[i];
        if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry();
            if (entry) {
                queue.push(entry);
            }
        }
    }

    // Process queue
    while (queue.length > 0) {
        const entry = queue.shift();
        if (entry.isFile) {
            fileEntries.push(entry);
        } else if (entry.isDirectory) {
            const reader = entry.createReader();
            const entries = await new Promise((resolve, reject) => {
                reader.readEntries(resolve, reject);
            });
            queue.push(...entries);
        }
    }

    return fileEntries;
};

// Helper to convert FileSystemFileEntry to File with path
const fileEntryToFile = (entry, path = '') => {
    return new Promise((resolve) => {
        entry.file((file) => {
            // Create a new File object with the relative path as a custom property
            const fileWithPath = file;
            Object.defineProperty(fileWithPath, 'webkitRelativePath', {
                writable: false,
                value: path
            });
            resolve(fileWithPath);
        });
    });
};

const CreateDataUpload = ({ onClose, createDataUpload, editingData, isAddingData = false, selectedContract }) => {
    const dispatch = useAppDispatch();

    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const formPopulatedRef = useRef(null);
    const [uploadName, setUploadName] = useState('');
    const fileSelectionMadeRef = useRef(false);
    // Debug uploadName state changes - keep minimal logging
    useEffect(() => {
        if (uploadName) {
            logger.debug('Dataset name updated:', uploadName);
        }
    }, [uploadName]);
    const [datasetId, setDatasetId] = useState('');
    const [actualDatasetName, setActualDatasetName] = useState(''); // Normalized DatasetName from API
    const [isCreatingDataset, setIsCreatingDataset] = useState(false);
    const [datasetNameError, setDatasetNameError] = useState('');
    const [tagName, setTagName] = useState('');
    const [tagValue, setTagValue] = useState('');
    const [addedTags, setAddedTags] = useState({});
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [limitsExceeded, setLimitsExceeded] = useState(false);
    const [limitExceededDetails, setLimitExceededDetails] = useState(null);
    const [showLimitsModal, setShowLimitsModal] = useState(false);
    const [tagError, setTagError] = useState('');
    const [skippedFilesCount, setSkippedFilesCount] = useState(0);
    // Debug log currentPage changes
    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    // State for filename error modal
    const [filenameErrorModalOpen, setFilenameErrorModalOpen] = useState(false);
    const [unsupportedFiles, setUnsupportedFiles] = useState([]);
    const disallowedFilenameChars = useSelector(selectDisallowedFilenameChars);
    useEffect(() => {
        const startIdx = (currentPage - 1) * itemsPerPage;
        const endIdx = currentPage * itemsPerPage;
        logger.debug(`Current page: ${currentPage}, showing files ${startIdx + 1}-${Math.min(endIdx, uploadedFiles.length)} of ${uploadedFiles.length}`);
    }, [currentPage, uploadedFiles.length, itemsPerPage]);

    // Calculate paginated files
    const paginatedFiles = useMemo(() => {
        const startIdx = (currentPage - 1) * itemsPerPage;
        const endIdx = currentPage * itemsPerPage;
        const sliced = uploadedFiles.slice(startIdx, endIdx);
        logger.debug(`Paginated files:`, sliced.length, 'files from index', startIdx, 'to', endIdx);
        return sliced;
    }, [uploadedFiles, currentPage, itemsPerPage]);

    // Calculate total pages
    const totalPages = useMemo(() => {
        return Math.ceil(uploadedFiles.length / itemsPerPage);
    }, [uploadedFiles.length, itemsPerPage]);

    // Pagination event handlers
    const handlePreviousPage = useCallback(() => {
        logger.debug('Previous clicked, current page:', currentPage);
        setCurrentPage(prev => Math.max(1, prev - 1));
    }, [currentPage]);

    const handleNextPage = useCallback(() => {
        logger.debug('Next clicked, current page:', currentPage);
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    }, [currentPage, totalPages]);

    const [isUploading, setIsUploading] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const [prefilledData, setPrefilledData] = useState({
        dataPartner: '',
        businessUnit: '',
        project: '',
        contract: '',
        contractId: ''
    });

    // Prefill data when component mounts or selectedContract changes
    useEffect(() => {
        if (selectedContract) {
            setPrefilledData({
                dataPartner: selectedContract.DataPartnerDisplayName || selectedContract.DataPartnerName || '',
                businessUnit: selectedContract.BusinessUnit || '',
                project: selectedContract.Project || '',
                contract: selectedContract.ContractDisplayName || selectedContract.Name || '',
                contractName: selectedContract.Name || '',  // ← API payload name
                contractId: selectedContract.ContractID || ''
            });
        }
    }, [selectedContract]);

    // For add data mode, prefill from editingData
    useEffect(() => {
        if (isAddingData && editingData) {
            setUploadName(editingData.datasetDisplayName || editingData.datasetName || '');
            setActualDatasetName(editingData.datasetName || '');

            if (editingData.CustomTags && typeof editingData.CustomTags === 'object' && !Array.isArray(editingData.CustomTags)) {
                setAddedTags(editingData.CustomTags);
            }
        }
    }, [isAddingData, editingData]);

    // Component lifecycle tracking
    useEffect(() => {
        return () => {
            // Clean up on unmount - no refs to clear since we removed the lock mechanism
        };
    }, []);

    // Initialize form state when editing data changes
    useEffect(() => {
        setDatasetNameError('');
    }, [isAddingData, editingData?.datasetId, editingData?.datasetName]);
    // Detect cancel by listening for focus event AFTER dialog closes

    const handleCancel = () => {

        // If upload is in progress, show cancel confirmation modal
        if (isUploading) {
            setShowCancelModal(true);
        } else {
            // If no upload in progress, just close
            onClose(false);
            setIsProcessingFiles(false);
        }
    };

    const handleConfirmCancel = () => {
        // No need to abort controllers - orchestrator handles this
        // Just update local file status for UI feedback
        setUploadedFiles(prev =>
            prev.map(file => {
                if (file.status === 'uploading' || file.status === 'pending' || file.status === 'finalizing') {
                    return { ...file, status: 'cancelled', error: 'Upload cancelled by user' };
                }
                return file;
            })
        );

        setIsUploading(false);
        setShowCancelModal(false);

        dispatch(addInfoNotification({
            message: 'Upload cancelled. You can close this window or use the data list to manage uploads.'
        }));
    };

    const handleClearAll = () => {
        // Always clear uploaded files and file-related pagination/limits
        setUploadedFiles([]);
        setCurrentPage(1);
        setLimitsExceeded(false);
        setLimitExceededDetails(null);
        setSkippedFilesCount(0);

        // When adding data to an existing dataset, keep the left-side
        // dataset metadata (name, BU, project, contract, tags) intact.
        if (isAddingData) {
            // It's still safe to clear any existing dataset name error
            // so the user can retry uploads without stale errors.
            setDatasetNameError('');
            return;
        }

        // In "create new dataset" mode, Clear all should reset the
        // entire form, including metadata and tags.
        // Clear text fields
        setUploadName('');
        setDatasetId('');
        setActualDatasetName('');
        setTagName('');
        setTagValue('');

        // Clear tags
        setAddedTags({});


        // Reset data partners selected state
        // setDataPartners(prev => prev.map(dp => ({
        //     ...dp,
        //     selected: false
        // })));

        // Clear created dataset from Redux
        dispatch(clearCreatedDataset());

        // Clear any dataset name error
        setDatasetNameError('');
    };

    const handleAddTag = () => {
        const trimmedName = tagName.trim();
        const trimmedValue = tagValue.trim();

        if (!trimmedName) {
            setTagError('Tag name is required');
            return;
        }
        if (!trimmedValue) {
            setTagError('Tag value is required');
            return;
        }
        // Disallow any whitespace in tag name
        if (!/^\S+$/.test(trimmedName)) {
            setTagError('Tag name cannot contain spaces. Use underscores instead.');
            return;
        }
        // Check max tag name length
        if (trimmedName.length > TAG_CONFIG.MAX_TAG_LENGTH) {
            setTagError(`Tag name must be ${TAG_CONFIG.MAX_TAG_LENGTH} characters or less`);
            return;
        }
        // Check max tag value length
        if (trimmedValue.length > TAG_CONFIG.MAX_TAG_LENGTH) {
            setTagError(`Tag value must be ${TAG_CONFIG.MAX_TAG_LENGTH} characters or less`);
            return;
        }
        if (!TAG_CONFIG.TAG_PATTERN.test(trimmedName)) {
            setTagError(`Tag name: ${TAG_CONFIG.TAG_PATTERN_MESSAGE}`);
            return;
        }
        if (!TAG_CONFIG.TAG_PATTERN.test(trimmedValue)) {
            setTagError(`Tag value: ${TAG_CONFIG.TAG_PATTERN_MESSAGE}`);
            return;
        }
        // Check max number of tags
        if (Object.keys(addedTags).length >= TAG_CONFIG.MAX_TAGS) {
            setTagError(`Tag limit reached. You can add up to ${TAG_CONFIG.MAX_TAGS} tags.`);
            return;
        }

        // Check for duplicate key (case-insensitive)
        if (Object.keys(addedTags).some(key => key.toLowerCase() === trimmedName.toLowerCase())) {
            setTagError('A tag with this name already exists');
            return;
        }

        // All validations passed - add key-value tag
        setAddedTags(prev => ({ ...prev, [trimmedName]: trimmedValue }));
        setTagName('');
        setTagValue('');
        setTagError('');
    };

    // Create dataset once before first upload
    const ensureDatasetExists = async () => {
        if (datasetId) {
            return { datasetId, datasetName: actualDatasetName };
        }

        setIsCreatingDataset(true);

        try {
            const datasetPayload = {
                DatasetDisplayName: uploadName.trim(),
                Project: prefilledData.project,
                BusinessUnit: prefilledData.businessUnit,
                ContractID: prefilledData.contractId,
                ContractType: selectedContract?.ContractType || '',
                DataProviderID: selectedContract?.DataProviderID || '',
                DataType: 'clinical',
                CustomTags: addedTags,
                description: `Dataset for ${uploadName}`,
            };

            const result = await dispatch(createDataset(datasetPayload)).unwrap();

            const newDatasetId = result.DatasetID;
            const newDatasetName = result.DatasetName || '';
            setDatasetId(newDatasetId);
            setActualDatasetName(newDatasetName);
            setDatasetNameError('');

            return { datasetId: newDatasetId, datasetName: newDatasetName };
        } catch (error) {
            console.error('Failed to create dataset:', error);

            // Extract the backend message - detail comes from rejectWithValue
            const backendMessage = error?.detail?.message || error?.message || 'Unknown error';
            const errorCode = error?.detail?.code || '';

            // Set inline field error for name conflict
            if (errorCode === ERROR_CODES.DATASET_NAME_CONFLICT) {
                const errorMsg = getErrorMessage(ERROR_CODES.DATASET_NAME_CONFLICT, backendMessage);
                setDatasetNameError(errorMsg);
            }

            // Re-throw with backend message preserved
            const enrichedError = new Error(`Dataset creation failed. ${backendMessage}`);
            enrichedError.isDatasetError = true;
            throw enrichedError;
        } finally {
            setIsCreatingDataset(false);
        }
    };
    
    const handleRemoveTag = (tagKeyToRemove) => {
        setAddedTags(prev => {
            const newTags = { ...prev };
            delete newTags[tagKeyToRemove];
            //remove tags error message if tagcount<=10
            if (Object.keys(newTags).length < TAG_CONFIG.MAX_TAGS) {
                setTagError('');
            }
            return newTags;
        });
    };

    const handleUploadClick = (type) => {
        setIsProcessingFiles(true);

        const input =
            type === 'folder'
                ? folderInputRef.current
                : fileInputRef.current;

        if (!input) {
            setIsProcessingFiles(false);
            return;
        }

        input.value = '';

        const onChangeHandler = () => {
            input.removeEventListener('change', onChangeHandler);
            input.removeEventListener('cancel', onCancelHandler);
        };

        const onCancelHandler = () => {
            setIsProcessingFiles(false);
            setUploadedFiles([]);

            input.removeEventListener('change', onChangeHandler);
            input.removeEventListener('cancel', onCancelHandler);
        };

        input.addEventListener('change', onChangeHandler, { once: true });
        input.addEventListener('cancel', onCancelHandler, { once: true });

        input.click();
    };

    // Drag and drop handlers
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        // Check if form is complete before allowing drop
        if (!uploadName) {
            dispatch(addWarningNotification({
                message: 'Please enter a dataset name before uploading files'
            }));
            return;
        }

        try {
            let files = [];

            // Check if we have DataTransferItemList (for folder support)
            if (e.dataTransfer.items) {
                const fileEntries = await getAllFileEntries(e.dataTransfer.items);

                // Convert all entries to File objects with paths
                const filePromises = fileEntries.map(entry => {
                    return fileEntryToFile(entry, entry.fullPath || entry.name);
                });

                files = await Promise.all(filePromises);
            } else {
                // Fallback for simple file drop
                files = Array.from(e.dataTransfer.files);
            }

            if (files.length > 0) {
                // Create a synthetic event to use handleFileChange logic
                const syntheticEvent = {
                    target: {
                        files: files,
                        value: ''
                    }
                };
                handleFileChange(syntheticEvent);
            }
        } catch (error) {
            console.error('Error processing dropped files:', error);
            dispatch(addErrorNotification({
                message: 'Error processing dropped files. Please try using the file/folder picker buttons.'
            }));
        }
    };
    const truncateFilename = (name, maxLength = 100) => {
        if (!name || name.length <= maxLength) return name;
        const extension = name.substring(name.lastIndexOf('.'));
        const start = name.substring(0, maxLength - extension.length - 3);
        return `${start}...${extension}`;
    };

    const handleFileChange = async (event) => {
        fileSelectionMadeRef.current = true;
        const files = Array.from(event.target.files);
        if (!files || files.length === 0) {
            setIsProcessingFiles(false);
            return;
        }
        logger.debug(`Files selected: ${files.length}`);

        // Check for unsupported filename characters
        const unsupported = getFilesWithUnsupportedNames(files, disallowedFilenameChars);
        if (unsupported.length > 0) {
            setUnsupportedFiles(unsupported);
            setFilenameErrorModalOpen(true);
            event.target.value = '';
            setIsProcessingFiles(false);
            return;
        }

        // First check upload limits before validation
        const limitCheck = validateUploadLimits(files);
        if (!limitCheck.isValid) {
            setLimitsExceeded(true);
            setLimitExceededDetails(limitCheck);
            setShowLimitsModal(true);
            setUploadedFiles([]);
            logger.warn('Upload limits exceeded:', limitCheck.exceededLimits);
            event.target.value = '';
            setIsProcessingFiles(false);
            return;
        }

        setLimitsExceeded(false);
        setLimitExceededDetails(null);

        // Validate files and get categorized results
        const { validFiles, skippedFiles, totalSkipped } = await validateFiles(files, {
            isExtensionValueAllowed,
            getFileExtension,
            isDangerousExtension
        });

        setSkippedFilesCount(totalSkipped);

        logger.debug(`Valid files: ${validFiles.length}, Skipped files: ${totalSkipped} (${skippedFiles.large.length} too large, ${skippedFiles.zeroSize.length} zero-size, ${skippedFiles.invalidExtension.length} invalid extension)`);

        const newFiles = validFiles.map((file) => {
            const relativePath = file.webkitRelativePath || file.name;
            return {
                id: uuidv4(),
                name: file.name,
                size: file.size,
                type: file.type,
                file: file,
                relativePath: relativePath,
                uploadProgress: 0,
                uploadedBytes: 0,
                status: 'pending',
                error: null
            };
        });

        if (totalSkipped > 0) {
            const message = buildSkipMessage(skippedFiles, newFiles.length === 0);
            if (newFiles.length === 0) {
                dispatch(addErrorNotification({ message }));
            } else {
                dispatch(addWarningNotification({ message }));
            }
        }

        if (newFiles.length > 0) {
            setUploadedFiles(newFiles);
            setCurrentPage(1);
        }

        event.target.value = '';
        setIsProcessingFiles(false);
    };
    const handleFolderChange = async (event) => {
        fileSelectionMadeRef.current = true;
        const files = Array.from(event.target.files);
        // Check for unsupported filename characters
        const unsupported = getFilesWithUnsupportedNames(files, disallowedFilenameChars);
        if (unsupported.length > 0) {
            setUnsupportedFiles(unsupported);
            setFilenameErrorModalOpen(true);
            event.target.value = '';
            setIsProcessingFiles(false);
            return;
        }
        if (!files || files.length === 0) {
            setIsProcessingFiles(false);
            return;
        }
        const limitCheck = validateUploadLimits(files);

        if (!limitCheck.isValid) {
            setLimitsExceeded(true);
            setLimitExceededDetails(limitCheck);
            setShowLimitsModal(true);
            setUploadedFiles([]);
            logger.warn('Upload limits exceeded:', limitCheck.exceededLimits);
            event.target.value = '';
            setIsProcessingFiles(false);
            return;
        }

        setLimitsExceeded(false);
        setLimitExceededDetails(null);

        const { validFiles, skippedFiles, totalSkipped } = await validateFiles(files, {
            isExtensionValueAllowed,
            getFileExtension,
            isDangerousExtension
        });

        setSkippedFilesCount(totalSkipped);

        const newFiles = validFiles.map((file) => {
            const relativePath = file.webkitRelativePath || file.name;
            return {
                id: uuidv4(),
                name: file.name,
                size: file.size,
                type: file.type,
                file: file,
                relativePath: relativePath,
                uploadProgress: 0,
                uploadedBytes: 0,
                status: 'pending',
                error: null
            };
        });

        if (totalSkipped > 0) {
            const message = buildSkipMessage(skippedFiles, newFiles.length === 0);
            if (newFiles.length === 0) {
                dispatch(addErrorNotification({ message }));
            } else {
                dispatch(addWarningNotification({ message }));
            }
        }

        if (newFiles.length > 0) {
            setUploadedFiles(newFiles);
            setCurrentPage(1);
        }

        event.target.value = '';
        setIsProcessingFiles(false);


    };

    const removeFile = (fileId) => {
        setUploadedFiles(prev => {
            const newFiles = prev.filter(file => file.id !== fileId);
            // Reset to page 1 if current page becomes empty after removal
            const maxPage = Math.ceil(newFiles.length / itemsPerPage);
            if (currentPage > maxPage && maxPage > 0) {
                setCurrentPage(maxPage);
            } else if (newFiles.length === 0) {
                setCurrentPage(1);
            }
            return newFiles;
        });
    };

    const handleCreateDataUpload = () => {
        logger.debug(' handleCreateDataUpload called');

        // Validate all required fields
        if (!uploadName || uploadName.trim() === '') {
            dispatch(addErrorNotification({
                message: 'Please enter a dataset name'
            }));
            return;
        }

        if (uploadedFiles.length === 0) {
            dispatch(addErrorNotification({
                message: 'Please select files to upload'
            }));
            return;
        }

        // Check if limits are exceeded
        if (limitsExceeded) {
            dispatch(addErrorNotification({
                message: 'Cannot upload: File selection exceeds upload limits. Please use CLI/API for large uploads.'
            }));
            setShowLimitsModal(true);
            return;
        }

        logger.debug('All validations passed, showing confirmation modal');
        // Show confirmation modal
        setShowConfirmModal(true);
    };

    const handleConfirmUpload = async () => {
        logger.debug('handleConfirmUpload called - starting upload process');
        setShowConfirmModal(false);

        try {
            setIsUploading(true);

            // Determine dataset ID and name based on mode
            let createdDatasetId;
            let actualDatasetNameToUse;

            if (isAddingData) {
                createdDatasetId = editingData.datasetId;
                actualDatasetNameToUse = editingData.datasetName;
            } else {
                // Create new dataset
                const datasetInfo = await ensureDatasetExists();
                createdDatasetId = datasetInfo.datasetId;
                actualDatasetNameToUse = datasetInfo.datasetName;
            }
            // Generate unique upload ID
            const uploadId = `upload_${uuidv4()}`;

            // Prepare upload context with prefilled data
            const uploadContext = {
                contractId: prefilledData.contractId,
                contractName: prefilledData.contractName || selectedContract?.Name || '',
                businessUnit: prefilledData.businessUnit,
                project: prefilledData.project,
                dataProviderName: selectedContract?.DataPartnerName || ''
            };

            // Start upload session
            dispatch(startUploadSession({
                uploadId,
                datasetName: actualDatasetNameToUse,
                datasetId: createdDatasetId,
                businessUnit: prefilledData.businessUnit,
                project: prefilledData.project,
                contract: selectedContract,
                dataProviderName: uploadContext.dataProviderName,
                skippedFilesCount: skippedFilesCount,
                files: uploadedFiles.map(file => ({
                    id: file.id,
                    name: file.name,
                    size: file.size,
                    relativePath: file.relativePath,
                    file: file.file
                }))
            }));
            // Start background upload
            startBackgroundUpload(
                uploadId,
                createdDatasetId,
                uploadedFiles.map(file => ({
                    id: file.id,
                    name: file.name,
                    size: file.size,
                    relativePath: file.relativePath,
                    file: file.file
                })),
                uploadContext
            );

            dispatch(addSuccessNotification({
                message: `Upload started for ${uploadedFiles.length} file(s). You can track progress in the data list.`
            }));

            setTimeout(() => {
                onClose(false);
            }, 1500);

        } catch (error) {
            console.error('Upload initiation error:', error);

            // Show the error message directly — enrichedError already has descriptive text
            dispatch(addErrorNotification({
                message: error.message || 'Failed to start upload.'
            }));
        } finally {
            setIsUploading(false);
        }
    };

    // Dynamic page title based on mode
    const pageTitle = isAddingData
        ? `Add Data to "${editingData?.datasetDisplayName || editingData?.datasetName || 'Dataset'}"`
        : 'Upload new data';

    return (
        <div className="data-upload-container">
            <SpinnerComponent
                isVisible={isUploading}
                overlay={true}
                message="Upload starting..."
            />
            <div className="upload-data-header fill--1">
                <h1 className='u-mt0 u-mb0'>{pageTitle}</h1>
            </div>
            <div className="flex user-groups-container">
                <div className="user-details-section u--1/4 fill--1" style={{
                    overflowY: Object.keys(addedTags).length > 0 ? 'auto' : 'visible',
                    maxHeight: Object.keys(addedTags).length > 0 ? 'calc(100vh - 150px)' : 'none',
                    paddingBottom: '80px'
                }}>
                    <div className=''>
                        <h4 className='u-mt0 u-mb--'>Input</h4>
                    </div>
                    <div>
                        <EDSTextField
                            name={"form-field"}
                            label={{ "width": "", "position": "top", "text": "Dataset Name" }}
                            placeholder={"Dataset name"}
                            instructionalText={isCreatingDataset ? "Creating dataset..." : datasetId ? "Dataset created ✓" : ""}
                            isRequired={true}
                            validationError={datasetNameError}
                            width={"100%"}
                            maxLength={50}
                            options={[]}
                            type={"text"}
                            value={uploadName}
                            onEDSTextFieldChange={(e) => {
                                setUploadName(e.target.value);
                                if (datasetNameError) {
                                    setDatasetNameError(''); // Clear error when user types
                                }
                            }}
                            isDisabled={isUploading || isAddingData}
                        ></EDSTextField>
                        {datasetNameError && (
                            <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                                {datasetNameError}
                            </div>
                        )}
                    </div>
                    {/* Contract - Disabled Text Field */}
                    <div className='u-mt'>
                        <EDSTextField
                            name={"contract"}
                            label={{ "width": "", "position": "top", "text": "Contract" }}
                            value={prefilledData.contract}
                            isDisabled={true}
                            width={"100%"}
                        />
                    </div>

                    {/* Business Unit - Disabled Text Field */}
                    <div className='u-mt'>
                        <EDSTextField
                            name={"business-unit"}
                            label={{ "width": "", "position": "top", "text": "Business Unit" }}
                            value={prefilledData.businessUnit}
                            isDisabled={true}
                            width={"100%"}
                        />
                    </div>

                    {/* Project - Disabled Text Field */}
                    <div className='u-mt'>
                        <EDSTextField
                            name={"project"}
                            label={{ "width": "", "position": "top", "text": "Project" }}
                            value={prefilledData.project}
                            isDisabled={true}
                            width={"100%"}
                        />
                    </div>

                    {/* Data Partner - Disabled Text Field */}
                    <div className='u-mt'>
                        <EDSTextField
                            name={"data-partner"}
                            label={{ "width": "", "position": "top", "text": "Data Partner" }}
                            value={prefilledData.dataPartner}
                            isDisabled={true}
                            width={"100%"}
                        />
                    </div>
                    <div className='u-mt'>
                        <div style={{
                            marginTop: '16px',
                            padding: '10px 12px',
                            backgroundColor: 'var(--eds-card-fill-color)',
                            borderLeft: '3px solid #0066cc',
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: 'var(--eds-body-text-color)'
                        }}>
                            <strong style={{ color: 'var(--eds-primary-text-color)' }}>💡 Important:</strong> {LOCKED_FILE_WARNING}
                        </div>
                    </div>
                </div>
                <div className="user-data-section u--3/4 fill--2">
                    <div>
                        <h4 className='u-mt0'>Browse</h4>
                    </div>
                    <div
                        className={`file-upload-card u-mt ${isDragging ? 'dragging' : ''}`}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <EDSIcon iconName={"ico-upload-32"}></EDSIcon>
                        <p className='text-bold'>Drag and drop or choose file(s) or folder</p>
                        <p>
                            {!uploadName
                                ? `${FILE_PROCESSING_WARNING}`
                                : `${FILE_UPLOAD_INSTRUCTION}`
                            }
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center'}}>
                            <div style={{
                                marginBottom: '12px',
                                padding: '10px 20px',
                                backgroundColor: '#e0e7f8',
                                borderLeft: '3px solid #0066cc',
                                borderRadius: '4px',
                                fontSize: '13px',
                                color: 'var(--eds-body-text-color)',
                                width: 'fit-content'
                            }}>
                                <strong style={{ color: 'var(--eds-primary-text-color)' }}>💡 Important:</strong> {PASSWORD_PROTECTED_FILE_WARNING}
                            </div>
                        </div>
                        <div className='file-upload-button' style={{ display: 'flex', gap: '10px' }}>
                            <EDSButton
                                variant={"primary"}
                                type={"button"}
                                label={"Choose files"}
                                isDisabled={!uploadName || isUploading}
                                onEDSButtonClick={() => handleUploadClick('file')}
                            ></EDSButton>
                            <EDSButton
                                variant={"primary"}
                                type={"button"}
                                label={"Choose folder"}
                                isDisabled={!uploadName || isUploading}
                                onEDSButtonClick={() => handleUploadClick('folder')}

                            ></EDSButton>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                                onCancel={() => {
                                    setIsProcessingFiles(false);
                                    setUploadedFiles([]);
                                }}

                            />
                            <input
                                ref={folderInputRef}
                                type="file"
                                webkitdirectory=""
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleFolderChange}
                                onCancel={() => {
                                    setIsProcessingFiles(false);
                                    setUploadedFiles([]);
                                }}

                            />
                        </div>
                    </div>

                    {isProcessingFiles ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '200px',
                            padding: '40px 8px'
                        }}>
                            <SpinnerComponent
                                isVisible={true}
                                overlay={false}
                                message="Loading file(s) or folder(s)..."
                            />
                        </div>
                    ) : uploadedFiles.length > 0 ? (
                        <div className="u-pt u-pb" style={{ borderBottom: '1px solid var(--eds-border-subtle-divider)', maxHeight: 'calc(100vh - 520px)', overflowY: 'auto', padding: '8px', borderRadius: '4px' }}>
                            {paginatedFiles.map((file) => (
                                <div key={file.id} style={{ marginBottom: '12px', padding: '12px', border: '1px solid var(--eds-border-subtle-divider)', borderRadius: '4px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        {/* shows a truncated filename/path with ellipsis for long names, */}
                                        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
                                            <div
                                                title={file.relativePath || file.name}
                                                style={{
                                                    fontWeight: '600',
                                                    marginBottom: '4px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',

                                                }}
                                            >
                                                {truncateFilename(file.relativePath || file.name)}
                                            </div>
                                            <div style={{ fontSize: '12px', color: 'var(--eds-body-text-color)', marginBottom: '8px' }}>
                                                {formatFileSize(file.size)} • {file.status}
                                            </div>
                                            {file.status === 'uploading' || file.status === 'finalizing' ? (
                                                <div>
                                                    <div style={{
                                                        width: '100%',
                                                        height: '6px',
                                                        backgroundColor: 'var(--eds-border-subtle-divider)',
                                                        borderRadius: '3px',
                                                        overflow: 'hidden',
                                                        marginBottom: '4px'
                                                    }}>
                                                        <div style={{
                                                            width: `${file.uploadProgress}%`,
                                                            height: '100%',
                                                            backgroundColor: file.status === 'finalizing' ? '#FFA500' : '#4CAF50',
                                                            transition: 'width 0.3s ease'
                                                        }}></div>
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: 'var(--eds-body-text-color)' }}>
                                                        {file.status === 'finalizing' ? 'Finalizing...' : `${formatFileSize(file.uploadedBytes)} / ${formatFileSize(file.size)} (${file.uploadProgress}%)`}
                                                    </div>
                                                </div>
                                            ) : file.status === 'completed' ? (
                                                <div style={{ color: '#4CAF50', fontSize: '12px' }}>✓ Completed</div>
                                            ) : file.status === 'cancelled' ? (
                                                <div style={{ color: '#FF9800', fontSize: '12px' }}>⏸️ Cancelled</div>
                                            ) : file.status === 'failed' ? (
                                                <div style={{ color: '#F44336', fontSize: '12px' }}>✗ Failed: {file.error}</div>
                                            ) : null}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            {(file.status === 'failed' || file.status === 'cancelled') && (
                                                <div style={{ fontSize: '11px', color: 'var(--eds-body-text-color)', fontStyle: 'italic' }}>
                                                    Use data list to retry
                                                </div>
                                            )}
                                            <EDSButton
                                                variant={"ghost"}
                                                type={"button"}
                                                label={""}
                                                icon={{ "name": "ico-remove-16" }}
                                                isDisabled={file.status === 'uploading' || file.status === 'finalizing'}
                                                onEDSButtonClick={() => removeFile(file.id)}
                                            ></EDSButton>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {!isProcessingFiles && uploadedFiles.length > itemsPerPage && (<div className="u-pt u-pb" style={{ borderTop: '1px solid var(--eds-border-subtle-divider)', paddingTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: '14px', color: 'var(--eds-body-text-color)' }}>
                            Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, uploadedFiles.length)} files of {uploadedFiles.length}
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <EDSButton
                                variant={"tertiary"}
                                type={"button"}
                                label={"Previous"}
                                icon={{}}
                                isDisabled={currentPage === 1}
                                onEDSButtonClick={handlePreviousPage}
                            ></EDSButton>
                            <span style={{ fontSize: '14px', minWidth: '100px', textAlign: 'center', fontWeight: '500' }}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <EDSButton
                                variant={"tertiary"}
                                type={"button"}
                                label={"Next"}
                                icon={{}}
                                isDisabled={currentPage >= totalPages}
                                onEDSButtonClick={handleNextPage}
                            ></EDSButton>
                        </div>
                    </div>
                    )}
                </div>
            </div>
            <div className='create-user-group-footer fill--1'>
                <div>
                    <EDSButton
                        variant={"ghost"}
                        type={"button"}
                        label={"Cancel"}
                        icon={{}}
                        onEDSButtonClick={handleCancel}
                    ></EDSButton>
                </div>
                <div>
                    <EDSButton
                        variant={"tertiary"}
                        type={"button"}
                        label={"Clear all"}
                        icon={{}}
                        isDisabled={isUploading}
                        onEDSButtonClick={handleClearAll}
                    ></EDSButton>
                </div>
                <div>
                    <EDSButton
                        variant={"primary"}
                        type={"button"}
                        label={isUploading ? "Uploading..." : "Upload Data"}
                        icon={{}}
                        isDisabled={
                            !uploadName ||
                            uploadedFiles.length === 0 ||
                            isUploading
                        }
                        onEDSButtonClick={handleCreateDataUpload}
                    ></EDSButton>
                </div>
            </div>

            {/* Confirmation  Modal */}
            <EDSModal
                variant={"standard"}
                isOpen={showConfirmModal}
                headerConfig={{ "text": "Confirm Upload", "useCancelIcon": true }}
                footerConfig={{ "acceptButton": { "label": "Confirm Upload", "type": "button" }, "cancelButton": { "label": "Cancel", "type": "button" } }}
                content={`<div slot='main-content' style='line-height: 1.5;'>Are you sure you want to upload ${uploadedFiles.length} file(s) to ${prefilledData?.project || 'this project'} for ${selectedContract?.ContractDisplayName || selectedContract?.Name || prefilledData?.contract || ''}?</div>`}
                width={"600px"}
                onEDSModalButtonClickAccept={handleConfirmUpload}
                onEDSModalButtonClickCancel={() => setShowConfirmModal(false)}
                onEDSModalClose={() => setShowConfirmModal(false)}
            ></EDSModal>

            {/* Cancel Upload Modal */}

            {/* Filename Error Modal */}
            <FilenameErrorModal
                isOpen={filenameErrorModalOpen}
                unsupportedFiles={unsupportedFiles}
                onClose={() => setFilenameErrorModalOpen(false)}
            />
            <EDSModal
                variant={"standard"}
                isOpen={showCancelModal}
                headerConfig={{ "text": "Cancel Upload", "useCancelIcon": true }}
                footerConfig={{ "acceptButton": { "label": "Yes, Cancel Upload", "type": "button" }, "cancelButton": { "label": "Continue Uploading", "type": "button" } }}
                content={`<div slot='main-content' style='line-height: 1.5;'>Are you sure you want to cancel the ongoing upload? All progress will be lost and files will need to be re-uploaded.</div>`}
                width={"600px"}
                onEDSModalButtonClickAccept={handleConfirmCancel}
                onEDSModalButtonClickCancel={() => setShowCancelModal(false)}
                onEDSModalClose={() => setShowCancelModal(false)}
            ></EDSModal>

            {/* Upload Limits Exceeded Modal */}
            <EDSModal
                variant={"standard"}
                isOpen={showLimitsModal}
                headerConfig={{
                    "text": "Upload Limits Exceeded",
                    "useCancelIcon": true
                }}
                footerConfig={{
                    "acceptButton": {
                        "label": "OK",
                        "type": "button"
                    }
                }}
                content={`
                    <div slot='main-content' style='line-height: 1.6;'>
                        <p style="margin-bottom: 16px;">
                            Your selection exceeds the upload limits.
                        </p>
                        <div style="background-color: #f5f5f5; padding: 12px; border-radius: 4px;">
                            <strong>Exceeded Limits:</strong>
                            <ul style="margin: 8px 0 0 20px; padding: 0;">
                                ${limitExceededDetails?.exceededLimits.map(limit =>
                    `<li>${limit.message}</li>`
                ).join('') || ''}
                            </ul>
                        </div>
                    </div>
                `}
                width={"600px"}
                onEDSModalButtonClickAccept={() => setShowLimitsModal(false)}
                onEDSModalClose={() => setShowLimitsModal(false)}
            ></EDSModal>

        </div>
    );
}

// Memoize the component to prevent unnecessary re-renders when parent updates
export default memo(CreateDataUpload, (prevProps, nextProps) => {
    // Only re-render if the essential props change
    const shouldSkipRerender = (
        prevProps.isAddingData === nextProps.isAddingData &&
        prevProps.editingData?.datasetId === nextProps.editingData?.datasetId &&
        prevProps.editingData?.datasetName === nextProps.editingData?.datasetName &&
        prevProps.onClose === nextProps.onClose &&
        prevProps.createDataUpload === nextProps.createDataUpload
    );

    return shouldSkipRerender;
});